import React from 'react';
import { motion } from 'framer-motion';
import { Check, Zap } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export function Process() {
  const { t } = useLanguage();
  const steps = t('process.steps');

  return (
    <section id="process" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900" />
      
      {/* Glow effects */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl -translate-y-1/2" />

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-primary font-mono text-sm tracking-wider uppercase mb-4 block">
            The process
          </span>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-4">
            {t('process.title')}
          </h2>
          <p className="text-xl text-muted-foreground">
            {t('process.subtitle')}
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical glowing line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px">
            <div className="h-full w-full bg-gradient-to-b from-primary via-purple-500 to-primary opacity-50" />
            <div className="absolute top-0 left-0 h-full w-full bg-gradient-to-b from-primary via-purple-500 to-primary blur-sm" />
          </div>

          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className={`relative flex items-center gap-8 mb-16 last:mb-0 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              }`}
              data-testid={`process-step-${index}`}
            >
              {/* Step Number Node */}
              <div className="absolute left-8 md:left-1/2 -translate-x-1/2 z-10">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center shadow-lg shadow-primary/30">
                    <span className="font-heading text-2xl font-bold text-white">{index + 1}</span>
                  </div>
                  {/* Pulse effect */}
                  <div className="absolute inset-0 rounded-full bg-primary/50 animate-ping opacity-30" />
                </div>
              </div>

              {/* Content Card */}
              <div className={`ml-24 md:ml-0 md:w-[45%] ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                <div className="bg-slate-900/80 border border-slate-800 backdrop-blur-xl p-6 md:p-8 rounded-2xl hover:border-primary/50 transition-all duration-300 group">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="font-heading text-xl md:text-2xl font-bold">{step.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-lg">{step.description}</p>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Final node */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="relative flex items-center gap-8 mb-16 last:mb-0 md:flex-row"
          >
            <div className="absolute left-8 md:left-1/2 -translate-x-1/2 z-10 w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30">
              <Zap className="text-white" size={28} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
